// The Room Detail Flyout's "Neighbors & walls" readout: for each wall this room shares with (or
// stands a cavity away from) another room on the same drawing — which Construction Standards
// rules apply, what the engine would put on it, and, when it would put nothing, why. It runs the
// engine's own wall fill (computeAutoWallSegments) over just this room's joints, so the readout
// cannot disagree with what the auto-wall sync actually does. Read-only; nothing is written.
import { computeAutoWallSegments, classifyConfidence, matchingConstructionRules, pairKey, wallSystemForPosition, sameWallSystem, DEFAULT_TUNABLES } from "./takeoffGeometry";

const isJoint = (e) => e.type === "shared" || e.type === "cavity";

/**
 * edges: detectEdges() output for the room's drawing. rooms: that drawing's rooms. rulesByPair: as RoomTakeoff
 * builds it (Map pairKey -> rules, active only) or null. wallSystemByRoom: Map RoomID -> effective wall system
 * entry (see RoomTakeoff). -> one row per joint, longest overlap first.
 */
export function neighborWallReadout({ roomId, edges = [], rooms = [], rulesByPair = null, wallSystemByRoom = null, tunables = DEFAULT_TUNABLES, predictWall = null }) {
  const mine = edges.filter(e => isJoint(e) && (e.a.RoomID === roomId || e.b.RoomID === roomId));
  if (!mine.length) return [];
  const segments = computeAutoWallSegments(rooms, mine, rulesByPair, tunables, { wallSystemByRoom, predictWall });
  return mine.map(e => {
    const other = e.a.RoomID === roomId ? e.b : e.a;
    const tier = classifyConfidence(e, tunables);
    const rules = rulesByPair && e.a.RoomTemplateID != null && e.b.RoomTemplateID != null
      ? matchingConstructionRules(e, rulesByPair.get(pairKey(e.a.RoomTemplateID, e.b.RoomTemplateID))) : [];
    const seg = segments.find(s => s.key.endsWith(`:${e.runKey}`) && s.key.startsWith(`${e.type}:`)
      && (s.roomId === roomId || s.roomBId === roomId));
    const fill = seg && seg.kind ? { kind: seg.kind, itemId: seg.itemId, assemblyId: seg.assemblyId, source: seg.source, ruleId: seg.ruleId, learned: seg.learned || null } : null;
    let reason = null;
    if (!fill) {
      const mineSys = wallSystemForPosition(wallSystemByRoom?.get(roomId), e.type);
      const theirs = wallSystemForPosition(wallSystemByRoom?.get(other.RoomID), e.type);
      if (tier !== "auto_apply") reason = `The boundary is "${tier.replace("_", " ")}" — confirm it in Review Walls first.`;
      else if (e.type === "shared" && mineSys && theirs && !sameWallSystem(mineSys, theirs)) reason = "The two rooms name different shared-wall systems — choose in Review Walls.";
      else if (rules.length > 1 && (rules[0].priority ?? 100) === (rules[1].priority ?? 100)) reason = "Several Construction Standards rules tie — give one a higher priority.";
      else reason = "No Construction Standards rule or wall system covers this wall.";
    }
    return {
      key: `${e.a.RoomID}-${e.b.RoomID}-${e.runKey}`, runKey: e.runKey,
      otherRoomId: other.RoomID, otherName: other.RoomName, otherTemplateId: other.RoomTemplateID ?? null,
      type: e.type, gapFt: e.gapFt, overlapFt: e.overlapFt, tier,
      rules, fill, reason,
    };
  }).sort((a, b) => (b.overlapFt || 0) - (a.overlapFt || 0));
}
